<template>
  <div
    :data-sb-automation="$attrs['data-sb-automation'] || 'sb-page'"
    data-sb-regression="sb-page"
    :class="$style['sb-page-container']"
  >
    <!--
      @slot Custom header
    -->
    <slot
      v-if="shouldShowHeader"
      name="header"
    >
      <SbPageHeader
        :title="title"
        :sub-title="subTitle"
        :breadcrumbs="breadcrumbs"
        :layout="layout"
      >
        <template
          v-if="$slots.title"
          #title
        >
          <!--
            @slot Custom title. Not available when header slot is used.
          -->
          <slot name="title" />
        </template>
        <template
          v-if="$slots.subtitle"
          #subtitle
        >
          <!--
            @slot Custom subtitle. Not available when header slot is used.
          -->
          <slot name="subtitle" />
        </template>
        <template
          v-if="$slots['call-to-action']"
          #call-to-action
        >
          <!--
            @slot Add a call to action to the header. Not available when header slot is used.
          -->
          <slot name="call-to-action" />
        </template>
        <template
          v-if="$slots.tabs"
          #tabs
        >
          <!--
            @slot Add tabs to the header. Not available when header slot is used.
          -->
          <slot name="tabs" />
        </template>
      </SbPageHeader>
    </slot>
    <div
      data-sb-automation="sb-page-content-container"
      :class="$style['sb-page']"
    >
      <div
        data-sb-automation="sb-page-content"
        :class="{
          [$style['sb-page__content']]: true,
          [$style['sb-page__content--narrow']]: layout === 'narrow',
          [$style['sb-page__content--wide']]: layout === 'wide',
        }"
      >
        <SbPanel
          v-if="variation !== 'bare'"
          :version="dsComponentDetails.version"
          :class="$style['sb-page__panel']"
          data-sb-automation="sb-page-content-panel"
        >
          <!--
            @slot Add custom content to the page.
          -->
          <slot
            name="content"
          />
        </SbPanel>
        <slot
          v-else
          name="content"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable css-modules/no-unused-class */
import { defineProps, defineOptions, computed, useSlots } from 'vue';
import SbPanel from '@src/components/Panel/SbPanel.vue';
import SbPageHeader from '@src/layout/PageHeader/SbPageHeader.vue';
import { useVersionedComponents } from '@src/mixins/versionedComponents';

defineOptions({
  name: 'SbPage',
});

/**
 * Default page frame will show tabs at the top and content within a panel to gain consistency for all pages that needs this layout. It will leverage existing SbPageHeader component and provide same slots plus 2 new ones (tabs + content)
*/
const props = defineProps({
  /**
     * The version of the component.
     * `legacy, next`
     */
  version: {
    type: String,
    default: undefined,
    validator: (value) => {
      return value.match(/(legacy|next)/);
    },
  },
  /**
     * Title of the Page
     */
  title: {
    type: String,
    required: true,
  },
  /**
     * Sub Title or the Description of the Page
     */
  subTitle: {
    type: String,
    default: '',
  },
  /**
     * Breadcrumbs to show above header.
     * Accepted Format: [{to: RouterLink, text: String}]
     */
  breadcrumbs: {
    type: Array,
    default: () => ([]),
  },
  /**
     * Define the layout of the contents
     * Accepted values: narrow, wide, fluid (default)
     */
  layout: {
    type: String,
    default: 'fluid',
    validator: (value) => {
      return value.match(/(narrow|wide|fluid)/);
    },
  },
  /**
   * Will define the style, layout, and behavior of the page
   * Default value is table
   * Accepted values: default, table, bare
   */
  variation: {
    type: String,
    default: 'table',
    validator: (value) => {
      return value.match(/(default|table|bare)/);
    },
  },
});

const slots = useSlots();
const { dsComponentDetails } = useVersionedComponents({
  componentVersionPropValue: props.version,
});
const shouldShowHeader = computed(() => {
  const check = (slots.header && slots.header())
   || props.title?.length > 0
   || props.subTitle?.length > 0
   || props.breadcrumbs?.length > 0
   || (slots.title && slots.title())
   || (slots.subtitle && slots.subtitle())
   || (slots['call-to-action'] && slots['call-to-action']())
   || (slots.tabs && slots.tabs());
  return !!check;
});
</script>

<style lang="scss" module>
@use '@src/layout/Page/style/sb-page.module.scss';
</style>
